import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '@scio3/ui';
import { fetchDesignById } from '../utils/ai-designer-api';
import type { DesignData, FontDefinition } from '../utils/ai-designer-api';

interface CanvasThumbnailProps {
  designId: string;
  onClick?: () => void;
}

/** Load Google Fonts via CSS link, custom fonts via FontFace API. */
async function loadFonts(fonts: FontDefinition[]): Promise<void> {
  if (!fonts.length) return;

  const googleFonts = fonts.filter((f) => f.type === 'google');
  const customFonts = fonts.filter((f) => f.type === 'custom');

  const promises: Promise<void>[] = [];

  if (googleFonts.length > 0) {
    const families = googleFonts.map((f) => f.family.replace(/ /g, '+')).join('&family=');
    const url = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;

    const promise = new Promise<void>((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = () => resolve();
      link.onerror = () => resolve();
      document.head.appendChild(link);
    });
    promises.push(promise);
  }

  for (const font of customFonts) {
    if (!font.url) continue;
    const urls = Array.isArray(font.url) ? font.url : [font.url];
    const src = urls.map((u) => `url("${u}")`).join(', ');

    try {
      const face = new FontFace(font.family, src);
      const loaded = await face.load();
      document.fonts.add(loaded);
    } catch {
      // Don't block on font failure
    }
  }

  if (promises.length > 0) {
    await Promise.all(promises);
    await document.fonts.ready;
  }
}

/**
 * Replace widget/repeater objects with transparent rects (same as Canvas 2.0).
 */
function processCanvasObjects(objects: unknown[]): unknown[] {
  if (!Array.isArray(objects)) return [];

  return objects.map((obj) => {
    const item = { ...(obj as Record<string, unknown>) };

    if (item.superType === 'widget' || item.superType === 'repeater') {
      item.type = 'rect';
      item.fill = 'rgba(0, 0, 0, 0)';
      delete item.src;
      delete item.text;
    }

    if (item.type === 'group' && Array.isArray(item.objects)) {
      item.objects = processCanvasObjects(item.objects as unknown[]);
    }

    return item;
  });
}

/**
 * Render a Fabric.js design as a thumbnail on a canvas element.
 * Dynamically imports fabric to keep it out of the main bundle (~300KB).
 */
async function renderThumbnail(
  canvasEl: HTMLCanvasElement,
  design: DesignData,
  containerWidth: number,
): Promise<void> {
  const { fabric } = await import('fabric');

  const canvasWidth = design.canvasWidth || 1920;
  const canvasHeight = design.canvasHeight || 1080;
  const scale = containerWidth / canvasWidth;
  const scaledWidth = Math.round(canvasWidth * scale);
  const scaledHeight = Math.round(canvasHeight * scale);

  const staticCanvas = new fabric.StaticCanvas(canvasEl, {
    width: scaledWidth,
    height: scaledHeight,
    renderOnAddRemove: false,
  });

  staticCanvas.setZoom(scale);

  const canvasData = {
    ...design.canvas,
    objects: processCanvasObjects(design.canvas.objects || []),
  };

  await new Promise<void>((resolve) => {
    staticCanvas.loadFromJSON(
      canvasData,
      () => {
        staticCanvas.renderAll();
        resolve();
      },
      (_obj: Record<string, unknown>, object: fabric.Object | undefined) => {
        if (!object) return;

        // Fix font wrapping for bold text (same as Canvas 2.0)
        if (object.name === 'text') {
          const textbox = object as fabric.Textbox;
          if (textbox.fontWeight === 'bold' || textbox.fontWeight === '700') {
            const originalHeight = (_obj as Record<string, number>).height ?? 0;
            let newHeight = textbox.height ?? 0;

            while (textbox.fontSize! > 10 && newHeight - originalHeight > 1) {
              textbox.fontSize = Math.max(10, textbox.fontSize! - 1);
              textbox.initDimensions();
              newHeight = textbox.height!;
            }
          }
        }

        // Fix null scale
        if (!object.scaleX) object.scaleX = object.scaleY || 1;
        if (!object.scaleY) object.scaleY = object.scaleX || 1;

        // Fabric v5 breaking changes — angle conversion
        const version = (_obj as Record<string, string>).version;
        if (version && parseInt(version.slice(0, 1), 10) < 5) {
          const obj = object as fabric.Object & { startAngle?: number; endAngle?: number };
          obj.startAngle = fabric.util.radiansToDegrees(obj.startAngle || 0);
          obj.endAngle = fabric.util.radiansToDegrees(obj.endAngle || 0);
        }
      },
    );
  });
}

/**
 * React component that renders an AI-generated design as a canvas thumbnail.
 * Measures its container width automatically and maintains correct aspect ratio.
 */
export function CanvasThumbnail({ designId, onClick }: CanvasThumbnailProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [aspectRatio, setAspectRatio] = useState<string>('16/9');
  const designDataRef = useRef<DesignData | null>(null);

  const doRender = useCallback(async () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const design = designDataRef.current;
    if (!container || !canvas || !design) return;

    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) return;

    await renderThumbnail(canvas, design, containerWidth);
    setStatus('ready');
  }, []);

  useEffect(() => {
    let disposed = false;

    async function init() {
      try {
        const design = await fetchDesignById(designId);
        if (disposed) return;

        designDataRef.current = design;

        // Set correct aspect ratio from design data
        const w = design.canvasWidth || 1920;
        const h = design.canvasHeight || 1080;
        setAspectRatio(`${w}/${h}`);

        // Load fonts before rendering
        if (design.editor?.fonts?.length) {
          await loadFonts(design.editor.fonts);
        }
        if (disposed) return;

        await doRender();
      } catch (error) {
        console.error('CanvasThumbnail render failed:', error);
        if (!disposed) setStatus('error');
      }
    }

    void init();
    return () => {
      disposed = true;
    };
  }, [designId, doRender]);

  return (
    <div
      ref={containerRef}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn('relative w-full overflow-hidden bg-muted/30', onClick && 'cursor-pointer')}
      style={{ aspectRatio }}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {/* Loading spinner */}
      {status === 'loading' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Canvas element */}
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        style={{ display: status === 'error' ? 'none' : 'block' }}
      />

      {/* Error fallback */}
      {status === 'error' && (
        <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
          {t('optibot.previewUnavailable')}
        </div>
      )}
    </div>
  );
}
